import React, { useEffect, useMemo, useState } from "react";
import { fetchCustomers } from "../services/customerService";

const COMKOD_LABELS = {
  1: "Telefon, direkt",
  2: "Fax",
  3: "E-post",
  5: "Hemtelefon",
  7: "Företags-e-post",
  di: "Telefon, direkt",
  f: "Telefax 2",
  mo: "Mobiltelefon",
  sm: "SMS",
  vx: "Telefonväxel",
};

const COMPANIES_PER_PAGE = 25;

function formatContactName(name) {
  if (!name) return "";
  return name.includes(",") ? name.split(",").reverse().join(" ").trim() : name;
}

function findEmail(...candidates) {
  const email = candidates.find(
    (candidate) => typeof candidate === "string" && candidate.includes("@"),
  );
  return email?.trim() || "";
}

function getNestedValue(value, ...keys) {
  return keys.reduce((current, key) => current?.[key], value);
}

// Flattens the nested company -> account -> contact -> communication tree.
function flattenCustomers(companies) {
  const contacts = [];

  (companies || []).forEach((company) => {
    (company.frkus || []).forEach((account) => {
      (account.kuskp || []).forEach((contact) => {
        const channels = (contact.kpcr || []).map((c) => ({
          id: c.id,
          code: c.comkod,
          label: COMKOD_LABELS[c.comkod] || `Kod ${c.comkod}`,
          value: c.comnr,
        }));

        const email = findEmail(
          ...channels.map((channel) => channel.value),
          contact.kpkpw?.qmagentoemail,
        );
        const phone = channels.find((c) => c.code === "1")?.value || "";
        const mobile = channels.find((c) => c.code === "mo")?.value || "";

        contacts.push({
          key: contact.occtooid || `${company.occtooid}-${contact.id}`,
          companyName: company.ftgnamn || "",
          companyNumber: company.ftgnr || "",
          orgNumber: company.orgnr || "",
          city: company.ftgpostadr3 || "",
          snikod: company.snikod || "",
          consentdate: company.consentdate || "",
          consent: company.consent || "",
          qsnigroupid: company.qsnigroupid || "",
          name: formatContactName(contact.ftgperson),
          contactId: contact.ftgkontaktnr || "",
          occtooId: contact.occtooid || "",
          email,
          phone,
          mobile,
          channels,
          channelsSource: "kpcr",
          webUser: contact.kpkpw || null,
          webUserSource: "kpkpw",
          sourceData: {
            kuskp: contact,
            kpcr: contact.kpcr,
            kpkpw: contact.kpkpw,
            kpwqmur: getNestedValue(contact, "kpkpw", "kpwqmur"),
          },
          account,
          company,
        });
      });
    });
  });

  return contacts;
}

function flattenContacts(contacts) {
  return (contacts || []).flatMap((contact360) => {
    const contact = contact360.kpkpwFtgKontaktNr?.[0];
    if (!contact) return [];

    const account = contact360.kuskpFtgNr?.[0] || null;
    const company = account?.frkus?.[0] || null;
    const channels = (contact.crkpKontaktNr || []).map((channel) => ({
      id: channel.id,
      code: channel.comkod,
      label: COMKOD_LABELS[channel.comkod] || `Kod ${channel.comkod}`,
      value: channel.comnr,
    }));
    const email = findEmail(
      ...channels.map((channel) => channel.value),
      contact360.qmagentoemail,
    );

    return [
      {
        key: contact.occtooid || contact360.occtooid || contact360.id,
        companyName: company?.ftgnamn || "",
        companyNumber: contact.ftgnr || company?.ftgnr || "",
        orgNumber: company?.orgnr || "",
        city: company?.ftgpostadr3 || "",
        name: formatContactName(contact.ftgperson),
        contactId: contact.ftgkontaktnr || contact360.ftgkontaktnr || "",
        occtooId: contact.occtooid || contact360.occtooid || "",
        email,
        phone: channels.find((channel) => channel.code === "1")?.value || "",
        mobile: channels.find((channel) => channel.code === "mo")?.value || "",
        channels,
        channelsSource: "crkpKontaktNr",
        webUser: contact360.kpwqmurId?.length
          ? {
              qmagentoemail: contact360.qmagentoemail,
              sqlidentity: contact360.sqlidentity,
              kpwqmur: contact360.kpwqmurId,
            }
          : contact360.qmagentoemail
            ? { qmagentoemail: contact360.qmagentoemail }
            : null,
        webUserSource: "kpwqmurId",
        sourceData: {
          kuskp: contact,
          kpcr: contact360.crkpKontaktNr,
          kpkpw: contact360,
          kpwqmur: contact360.kpwqmurId,
        },
        account,
        company,
      },
    ];
  });
}

function groupContactsByCompany(contacts) {
  const groups = new Map();

  contacts.forEach((contact) => {
    const companyKey =
      contact.companyNumber ||
      contact.account?.occtooid ||
      contact.company?.occtooid ||
      contact.companyName ||
      contact.key;
    const contactKey = `${companyKey}-${contact.contactId || contact.key}`;

    if (!groups.has(companyKey)) {
      groups.set(companyKey, {
        key: companyKey,
        companyName: contact.companyName,
        companyNumber: contact.companyNumber,
        orgNumber: contact.orgNumber,
        city: contact.city,
        snikod: contact.snikod,
        consentdate: contact.consentdate,
        consent: contact.consent,
        qsnigroupid: contact.qsnigroupid,
        occtooId: contact.account?.occtooid || contact.company?.occtooid || "",
        contacts: new Map(),
      });
    }

    groups.get(companyKey).contacts.set(contactKey, contact);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    contacts: Array.from(group.contacts.values()),
  }));
}

function matchesSearch(group, term) {
  if (!term) return true;
  const needle = term.toLowerCase();
  return [
    group.companyName,
    group.companyNumber,
    group.orgNumber,
    group.occtooId,
    group.snikod,
    group.consentdate,
    group.consent,
    group.qsnigroupid,
    ...group.contacts.flatMap((contact) => [
      contact.name,
      contact.contactId,
      contact.occtooId,
      contact.email,
      contact.phone,
      contact.mobile,
    ]),
  ].some((v) => v && String(v).toLowerCase().includes(needle));
}

function getEmailPreview(group) {
  return group.contacts
    .map((contact) => contact.email)
    .filter((email) => typeof email === "string" && email.includes("@"))
    .slice(0, 2)
    .join(" · ");
}

function SourceTag({ name }) {
  if (!name) return null;
  return (
    <span
      className="badge bg-light text-secondary border ms-2"
      title={`Data från fältet "${name}" i källsystemet`}
    >
      {name}
    </span>
  );
}

function SourceValue({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted">-</span>;
  }

  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-muted">Tom lista</span>;
    return (
      <div className="d-flex flex-column gap-2">
        {value.map((item, index) => (
          <div className="border-start ps-2" key={item?.id || index}>
            <SourceValue value={item} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) return <span className="text-muted">Tomt objekt</span>;
    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0">
          <tbody>
            {entries.map(([key, childValue]) => (
              <tr key={key}>
                <th style={{ width: "30%" }}>{key}</th>
                <td>
                  <SourceValue value={childValue} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (typeof value === "boolean") return value ? "Ja" : "Nej";
  return String(value);
}

function SourceData({ sourceData }) {
  const sections = Object.entries(sourceData || {}).filter(
    ([, value]) => value !== null && value !== undefined,
  );
  if (!sections.length) return null;

  return (
    <div className="mt-4">
      <h6>Källdata</h6>
      {sections.map(([name, value]) => (
        <details className="border rounded mb-2" key={name}>
          <summary className="fw-bold p-2">{name}</summary>
          <div className="p-2 pt-0">
            <SourceValue value={value} />
          </div>
        </details>
      ))}
    </div>
  );
}

function ContactDetails({ contact }) {
  if (!contact) return null;

  return (
    <div className="card mt-3">
      <div className="card-body">
        <h5>{contact.name || "Okänd kontakt"}</h5>
        <div className="row mb-3">
          <div className="col-md-12">
            <div>
              <strong>Kontakt-ID:</strong> {contact.contactId || "-"}
            </div>
          </div>
        </div>

        <h6>
          Kommunikationskanaler <SourceTag name={contact.channelsSource} />
        </h6>
        {contact.channels.length ? (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Typ</th>
                <th>Värde</th>
              </tr>
            </thead>
            <tbody>
              {contact.channels.map((channel) => (
                <tr key={channel.id}>
                  <td>{channel.label}</td>
                  <td>{channel.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-muted">Inga kommunikationskanaler.</div>
        )}

        {contact.webUser && (
          <>
            <h6>
              Webbanvändare <SourceTag name={contact.webUserSource} />
            </h6>
            <div>E-post: {contact.webUser.qmagentoemail || "-"}</div>
            <div>SQL-identity: {contact.webUser.sqlidentity || "-"}</div>
            {contact.webUser.kpwqmur?.length > 0 && (
              <table className="table table-sm mt-2">
                <thead>
                  <tr>
                    <th>Webbutik</th>
                    <th>Admin</th>
                    <th>Roll-ID</th>
                  </tr>
                </thead>
                <tbody>
                  {contact.webUser.kpwqmur.map((role) => (
                    <tr key={role.id}>
                      <td>{role.qwebstoreid}</td>
                      <td>{role.qmagentoadmin === "1" ? "Ja" : "Nej"}</td>
                      <td>{role.qmagentoroleid}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        <SourceData sourceData={contact.sourceData} />
      </div>
    </div>
  );
}

const CONTACTS_PER_PAGE = 25;

function matchesContactSearch(contact, term) {
  if (!term) return true;
  const needle = term.toLowerCase();
  return [
    contact.name,
    contact.contactId,
    contact.occtooId,
    contact.email,
    contact.phone,
    contact.mobile,
  ].some((v) => v && String(v).toLowerCase().includes(needle));
}

function CompanyDetails({ group, selectedContactKey, onSelectContact }) {
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [contactPage, setContactPage] = useState(1);

  useEffect(() => {
    setContactSearchTerm("");
    setContactPage(1);
  }, [group?.key]);

  useEffect(() => {
    setContactPage(1);
  }, [contactSearchTerm]);

  const selectedContact = useMemo(
    () =>
      group?.contacts.find((contact) => contact.key === selectedContactKey) ||
      null,
    [group, selectedContactKey],
  );

  const filteredContacts = useMemo(
    () =>
      (group?.contacts || []).filter((contact) =>
        matchesContactSearch(contact, contactSearchTerm),
      ),
    [group, contactSearchTerm],
  );

  const totalContactPages = Math.max(
    1,
    Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE),
  );
  const paginatedContacts = useMemo(() => {
    const start = (contactPage - 1) * CONTACTS_PER_PAGE;
    return filteredContacts.slice(start, start + CONTACTS_PER_PAGE);
  }, [contactPage, filteredContacts]);

  if (!group) return null;

  return (
    <div className="card mt-3">
      <div className="card-body">
        <h4>{group.companyName || "Okänt företag"}</h4>
        <div className="text-muted mb-3">
          Företagsnummer: {group.companyNumber || "-"}
          {group.occtooId && ` · Occtoo-ID: ${group.occtooId}`}
        </div>
        <div className="mb-4">
          <strong>Org.nr:</strong> {group.orgNumber || "-"} ·{" "}
          <strong>Ort:</strong> {group.city || "-"}
        </div>
        <div className="row mb-4">
          <div className="col-md-3"><strong>SNIKOD:</strong> {group.snikod || "-"}</div>
          <div className="col-md-3"><strong>Samtyckesdatum:</strong> {group.consentdate || "-"}</div>
          <div className="col-md-3"><strong>Consent på företagsnivå:</strong> {group.consent || "-"}</div>
          <div className="col-md-3"><strong>Q-SNI-grupp-ID:</strong> {group.qsnigroupid || "-"}</div>
        </div>

        <h5>Kontakter ({group.contacts.length})</h5>

        {!selectedContact && (
          <input
            className="form-control mb-2"
            placeholder="Sök på namn, kontakt-ID, telefon eller e-post"
            value={contactSearchTerm}
            onChange={(e) => setContactSearchTerm(e.target.value)}
          />
        )}

        <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
          <span>
            {selectedContact
              ? "1 vald kontakt"
              : `${filteredContacts.length} kontakter`}
          </span>
          {selectedContact && (
            <span>Klicka på kontakten igen för att visa alla</span>
          )}
        </div>

        <div
          className="list-group"
          style={{ maxHeight: "40vh", minHeight: 80, overflowY: "auto" }}
        >
          {(selectedContact ? [selectedContact] : paginatedContacts).map(
            (contact) => (
              <button
                key={contact.key}
                type="button"
                className="list-group-item list-group-item-action"
                aria-pressed={contact.key === selectedContactKey}
                style={
                  contact.key === selectedContactKey
                    ? {
                        backgroundColor: "#ffe4c2",
                        borderColor: "#f0b36b",
                        color: "#3f2a16",
                      }
                    : undefined
                }
                onClick={() => onSelectContact(contact.key)}
              >
                <strong>{contact.name || "Okänd kontakt"}</strong>
                <div className="small text-muted">
                  Kontakt-ID: {contact.contactId || "-"} ·{" "}
                  {contact.email || contact.phone || "Ingen kontaktuppgift"}
                </div>
              </button>
            ),
          )}
          {!filteredContacts.length && (
            <div className="text-muted p-2">Inga kontakter att visa.</div>
          )}
        </div>

        {!selectedContact && filteredContacts.length > CONTACTS_PER_PAGE && (
          <nav aria-label="Sidnavigering för kontakter" className="mt-3">
            <ul className="pagination pagination-sm justify-content-center align-items-center">
              <li
                className={`page-item ${contactPage === 1 ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() =>
                    setContactPage((page) => Math.max(1, page - 1))
                  }
                  disabled={contactPage === 1}
                >
                  Föregående
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">
                  Sida {contactPage} av {totalContactPages}
                </span>
              </li>
              <li
                className={`page-item ${contactPage === totalContactPages ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() =>
                    setContactPage((page) =>
                      Math.min(totalContactPages, page + 1),
                    )
                  }
                  disabled={contactPage === totalContactPages}
                >
                  Nästa
                </button>
              </li>
            </ul>
          </nav>
        )}

        <ContactDetails contact={selectedContact} />
      </div>
    </div>
  );
}

export default function CustomerContactPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedContactKey, setSelectedContactKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const contacts = useMemo(() => companies, [companies]);

  const filteredContacts = useMemo(
    () => contacts.filter((c) => matchesSearch(c, searchTerm)),
    [contacts, searchTerm],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredContacts.length / COMPANIES_PER_PAGE),
  );
  const paginatedContacts = useMemo(() => {
    const start = (currentPage - 1) * COMPANIES_PER_PAGE;
    return filteredContacts.slice(start, start + COMPANIES_PER_PAGE);
  }, [currentPage, filteredContacts]);

  const selectedContact = useMemo(
    () => contacts.find((c) => c.key === selectedKey) || null,
    [contacts, selectedKey],
  );

  const loadCustomers = async (term = "") => {
    try {
      setLoading(true);
      setError("");
      setSelectedKey(null);
      setSelectedContactKey(null);
      setCurrentPage(1);
      const data = await fetchCustomers(term);
      const nestedContacts = flattenCustomers(data?.results);
      const directContacts = flattenContacts(data?.contacts);
      const uniqueContacts = new Map(
        [...nestedContacts, ...directContacts].map((contact) => [
          contact.key,
          contact,
        ]),
      );
      setCompanies(groupContactsByCompany([...uniqueContacts.values()]));
    } catch (e) {
      console.error(e);
      setError(`Fel vid hämtning av kunder: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSearch = () => loadCustomers(searchTerm);

  return (
    <div className="container mt-4">
      <h1>Kundkontakter</h1>

      <div className="mb-3 d-flex gap-2">
        <input
          className="form-control"
          placeholder="Sök på namn, kundnr, ID, telefon eller e-post"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <button
          className="btn btn-dark text-nowrap"
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? "Söker…" : "Hämta"}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div>
        <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
          <span>
            {selectedContact
              ? "1 vald kund"
              : `${filteredContacts.length} kunder`}
          </span>
          {selectedContact && (
            <span>Klicka på kunden igen för att visa alla</span>
          )}
        </div>
        <div
          className="list-group border rounded"
          style={{ maxHeight: "50vh", minHeight: 120, overflowY: "auto" }}
        >
          {(selectedContact ? [selectedContact] : paginatedContacts).map(
            (c) => (
              <button
                key={c.key}
                type="button"
                className="list-group-item list-group-item-action"
                aria-pressed={c.key === selectedKey}
                style={
                  c.key === selectedKey
                    ? {
                        backgroundColor: "#ffe4c2",
                        borderColor: "#f0b36b",
                        color: "#3f2a16",
                      }
                    : undefined
                }
                onClick={() => {
                  setSelectedKey(selectedKey === c.key ? null : c.key);
                  setSelectedContactKey(null);
                }}
              >
                <div className="fw-bold">
                  {c.companyName || "Okänt företag"}
                </div>
                <div className="small">
                  Företagsnr: {c.companyNumber || "-"} · {c.contacts.length}{" "}
                  kontakter
                </div>
                {getEmailPreview(c) && (
                  <div className="small text-muted">{getEmailPreview(c)}</div>
                )}
              </button>
            ),
          )}
          {!loading && !filteredContacts.length && (
            <div className="text-muted p-2">Inga kontakter att visa.</div>
          )}
        </div>

        {!selectedContact && filteredContacts.length > COMPANIES_PER_PAGE && (
          <nav aria-label="Sidnavigering för kunder" className="mt-3">
            <ul className="pagination pagination-sm justify-content-center align-items-center">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                  disabled={currentPage === 1}
                >
                  Föregående
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">
                  Sida {currentPage} av {totalPages}
                </span>
              </li>
              <li
                className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Nästa
                </button>
              </li>
            </ul>
          </nav>
        )}

        <div>
          {selectedContact ? (
            <CompanyDetails
              group={selectedContact}
              selectedContactKey={selectedContactKey}
              onSelectContact={(contactKey) =>
                setSelectedContactKey((currentKey) =>
                  currentKey === contactKey ? null : contactKey,
                )
              }
            />
          ) : (
            <div className="text-muted mt-3">
              Välj ett företag för att se kontakter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
